import { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useCategories, useProducts } from "../hooks/useCatalog";

export default function Categories() {
  const { data: categories = [], isLoading, isError } = useCategories();
  const { data: products = [] } = useProducts();

  const productCounts = useMemo(
    () =>
      products.reduce((counts, product) => {
        counts[product.category] = (counts[product.category] || 0) + 1;
        return counts;
      }, {}),
    [products],
  );

  const showSkeleton = isLoading && categories.length === 0;

  return (
    <main className="mx-auto min-h-[70vh] max-w-7xl px-4 pb-28 pt-8 md:px-6 md:pb-12">
      <div className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-700">
          Explore the store
        </p>
        <h1 className="mt-2 font-serif text-4xl leading-none text-stone-950 md:text-5xl">
          Shop by category
        </h1>
        <p className="mt-3 text-sm leading-6 text-stone-600">
          Find everyday essentials, fresh picks, and more from Quick Door Now.
        </p>
      </div>

      {isError && categories.length === 0 && (
        <section
          className="mt-8 border border-red-200 bg-red-50 p-5 text-sm text-red-800"
          data-testid="categories-error"
        >
          Categories are unavailable right now. Please try again.
        </section>
      )}

      <section
        className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
        data-testid="visual-category-grid"
      >
        {showSkeleton
          ? [1, 2, 3, 4, 5, 6, 7, 8].map((placeholder) => (
              <div
                key={placeholder}
                data-testid="category-skeleton"
                className="animate-pulse rounded-2xl bg-stone-100 p-3"
              >
                <div className="aspect-square w-full rounded-xl bg-stone-200" />
                <div className="mt-3 h-3 w-2/3 rounded bg-stone-200" />
                <div className="mt-2 h-2 w-1/3 rounded bg-stone-200" />
              </div>
            ))
          : categories.map((category) => {
              const image =
                category.image ||
                products.find((product) => product.category === category.key)?.image;
              return (
                <Link
                  key={category.key}
                  to={`/c/${category.key}`}
                  className={
                    "group relative overflow-hidden rounded-2xl bg-[#eef6ef] p-3 shadow-sm " +
                    "transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 " +
                    "hover:border-orange-300 hover:shadow-lg focus-visible:outline focus-visible:outline-2 " +
                    "focus-visible:outline-offset-2 focus-visible:outline-orange-600"
                  }
                  data-testid={`category-card-${category.key}`}
                >
                  {image && (
                    <img
                      src={image}
                      alt=""
                      className="aspect-square w-full rounded-xl object-cover"
                    />
                  )}
                  <h2 className="mt-3 text-sm font-bold text-stone-950">{category.name}</h2>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-stone-500">{productCounts[category.key] || 0} items</span>
                    <ArrowRight className="h-4 w-4 text-orange-600 transition-transform duration-200 group-hover:translate-x-1" />
                  </div>
                </Link>
              );
            })}
      </section>
    </main>
  );
}
